import { FormEvent, useEffect, useRef, useState } from 'react';

import { getErrorMessage } from '@/hooks/useApi';

import {
  CreateGiftCodesInput,
  CreateGiftCodesResponse,
  GiftCode,
  useCreateGiftCodes,
  useListGiftCodes,
  useRevokeGiftCode,
} from './useGiftCodes';

export function GiftCodesPage() {
  const [quantity, setQuantity] = useState<number | ''>(1);
  const [tokensToGrant, setTokensToGrant] = useState<number | ''>(25);
  const [campaign, setCampaign] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastCreate, setLastCreate] = useState<{
    input: CreateGiftCodesInput;
    result: CreateGiftCodesResponse;
  } | null>(null);
  const [codeToRevoke, setCodeToRevoke] = useState<string | null>(null);

  const createGiftCodes = useCreateGiftCodes();
  const revokeGiftCode = useRevokeGiftCode();
  const { data: giftCodes, isLoading, error: listError } = useListGiftCodes();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLastCreate(null);

    // Validate input
    if (
      typeof quantity !== 'number' ||
      !Number.isInteger(quantity) ||
      quantity < 1 ||
      quantity > 100
    ) {
      setError('Quantity must be between 1 and 100');
      return;
    }

    if (
      typeof tokensToGrant !== 'number' ||
      !Number.isInteger(tokensToGrant) ||
      tokensToGrant < 1 ||
      tokensToGrant > 10000
    ) {
      setError('Tokens per code must be between 1 and 10,000');
      return;
    }

    const input: CreateGiftCodesInput = {
      quantity,
      tokensToGrant,
      ...(campaign.trim() && { campaign: campaign.trim() }),
    };

    try {
      const result = await createGiftCodes.mutateAsync(input);
      setLastCreate({ input, result });
      // Reset form
      setQuantity(1);
      setTokensToGrant(25);
      setCampaign('');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleRevoke = async (code: string) => {
    try {
      await revokeGiftCode.mutateAsync(code);
      setCodeToRevoke(null);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const [copiedText, setCopiedText] = useState<string | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear timeout on unmount to avoid setting state after unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopiedText(null), 2000);
    } catch (_error) {
      // Clipboard write failed (permissions, unsupported context)
    }
  };

  const getStatusBadge = (status: GiftCode['status']) => {
    switch (status) {
      case 'active':
        return (
          <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
            Active
          </span>
        );
      case 'used':
        return (
          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
            Used
          </span>
        );
      case 'revoked':
        return (
          <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
            Revoked
          </span>
        );
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Gift Codes</h1>
        <p className="mt-1 text-slate-600">
          Create and manage gift codes that grant tokens to users
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Create Form */}
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Create Gift Codes</h2>

          {error && (
            <div role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-slate-700">
                Quantity <span className="text-red-500">*</span>
              </label>
              <input
                id="quantity"
                type="number"
                value={quantity}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!raw) {
                    setQuantity('');
                    return;
                  }
                  const parsed = parseInt(raw, 10);
                  setQuantity(Number.isNaN(parsed) ? '' : parsed);
                }}
                min={1}
                max={100}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-slate-500">Number of codes to create (1-100)</p>
            </div>

            <div>
              <label htmlFor="tokensToGrant" className="block text-sm font-medium text-slate-700">
                Tokens per Code <span className="text-red-500">*</span>
              </label>
              <input
                id="tokensToGrant"
                type="number"
                value={tokensToGrant}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!raw) {
                    setTokensToGrant('');
                    return;
                  }
                  const parsed = parseInt(raw, 10);
                  setTokensToGrant(Number.isNaN(parsed) ? '' : parsed);
                }}
                min={1}
                max={10000}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-slate-500">
                Tokens granted when code is redeemed (1-10,000)
              </p>
            </div>

            <div>
              <label htmlFor="campaign" className="block text-sm font-medium text-slate-700">
                Campaign (optional)
              </label>
              <input
                id="campaign"
                type="text"
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                maxLength={100}
                placeholder="e.g., beta-launch, holiday-2026"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-slate-500">Tag codes for tracking purposes</p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={createGiftCodes.isPending}
                className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:bg-indigo-400"
              >
                {createGiftCodes.isPending ? 'Creating...' : 'Create Gift Codes'}
              </button>
            </div>
          </form>
        </div>

        {/* Success Result */}
        {lastCreate && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6">
            <h2 className="mb-4 text-lg font-semibold text-green-900">Codes Created</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-green-700">Quantity</dt>
                <dd className="mt-1 text-lg font-bold text-green-900">
                  {lastCreate.result.codes.length} codes
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-green-700">Tokens per Code</dt>
                <dd className="mt-1 text-lg font-semibold text-green-900">
                  {lastCreate.result.tokensPerCode} tokens
                </dd>
              </div>
              {lastCreate.input.campaign && (
                <div>
                  <dt className="text-sm font-medium text-green-700">Campaign</dt>
                  <dd className="mt-1 text-sm text-green-900">{lastCreate.input.campaign}</dd>
                </div>
              )}
              <div>
                <dt className="mb-2 text-sm font-medium text-green-700">Generated Codes</dt>
                <dd className="max-h-48 space-y-1 overflow-y-auto">
                  {lastCreate.result.codes.map((code) => (
                    <div
                      key={code}
                      className="flex items-center justify-between rounded bg-green-100 px-2 py-1 font-mono text-xs"
                    >
                      <span className="text-green-900">{code}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(code)}
                        className="text-green-700 hover:text-green-900"
                        title="Copy to clipboard"
                      >
                        {copiedText === code ? (
                          <span className="text-xs font-medium">Copied!</span>
                        ) : (
                          <CopyIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </dd>
              </div>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => copyToClipboard(lastCreate.result.codes.join('\n'))}
                  className="w-full rounded-lg border border-green-300 bg-green-100 px-4 py-2 text-sm text-green-800 hover:bg-green-200"
                >
                  {copiedText === lastCreate.result.codes.join('\n') ? 'Copied!' : 'Copy All Codes'}
                </button>
              </div>
            </dl>
          </div>
        )}

        {/* Info Panel (when no success result) */}
        {!lastCreate && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
            <h2 className="mb-4 text-lg font-semibold text-slate-900">About Gift Codes</h2>
            <div className="space-y-3 text-sm text-slate-600">
              <p>
                Gift codes are unique UUIDs that grant tokens to users when redeemed. They can be
                used for promotions, beta testing rewards, or customer support.
              </p>
              <p>
                <strong>One-time use:</strong> Each code can only be redeemed once by a single user.
              </p>
              <p>
                <strong>Revokable:</strong> Active codes can be revoked if needed (e.g., if
                compromised).
              </p>
              <p>
                <strong>Trackable:</strong> Use campaign tags to track which codes are used and
                when.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Gift Codes List */}
      <div className="mt-8 rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Gift Codes</h2>
        </div>

        {isLoading ? (
          <div className="p-6 text-center text-slate-500">Loading gift codes...</div>
        ) : listError ? (
          <div className="p-6 text-center text-red-600">
            Failed to load gift codes: {getErrorMessage(listError)}
          </div>
        ) : !giftCodes || giftCodes.length === 0 ? (
          <div className="p-6 text-center text-slate-500">No gift codes created yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-left text-sm text-slate-600">
                <tr>
                  <th className="px-6 py-3 font-medium">Code</th>
                  <th className="px-6 py-3 font-medium">Tokens</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Campaign</th>
                  <th className="px-6 py-3 font-medium">Created</th>
                  <th className="px-6 py-3 font-medium">Used By</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {giftCodes.map((code) => (
                  <tr key={code.code} className="hover:bg-slate-50">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-slate-900">
                          {code.code.slice(0, 8)}...
                        </span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(code.code)}
                          className="text-slate-400 hover:text-slate-600"
                          title="Copy full code"
                        >
                          {copiedText === code.code ? (
                            <span className="text-xs font-medium text-green-600">Copied!</span>
                          ) : (
                            <CopyIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-slate-900">{code.tokensToGrant}</td>
                    <td className="px-6 py-3">{getStatusBadge(code.status)}</td>
                    <td className="px-6 py-3 text-sm text-slate-600">{code.campaign || '-'}</td>
                    <td className="px-6 py-3 text-sm text-slate-600">
                      {new Date(code.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-slate-600">
                      {code.usedBy ? (
                        <span className="font-mono text-xs">{code.usedBy.slice(0, 8)}...</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {code.status === 'active' && (
                        <button
                          type="button"
                          onClick={() => setCodeToRevoke(code.code)}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Revoke Confirmation Modal */}
      {codeToRevoke && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <h3 className="text-lg font-semibold text-slate-900">Revoke Gift Code?</h3>
            <p className="mt-2 text-sm text-slate-600">
              This will permanently deactivate the code. It cannot be redeemed after revocation.
            </p>
            <p className="mt-2 font-mono text-sm text-slate-900">{codeToRevoke}</p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setCodeToRevoke(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleRevoke(codeToRevoke)}
                disabled={revokeGiftCode.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:bg-red-400"
              >
                {revokeGiftCode.isPending ? 'Revoking...' : 'Revoke Code'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}
